import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  ArrowRight, CheckCircle2, Database, Users, GitBranch, BarChart2,
  ShieldCheck, Clock, Award, Zap, Settings, TrendingUp
} from 'lucide-react';

const PharmaceuticalMarketingAnalytics = () => {
  const metrics = [
    { value: '100%', label: 'Automation', description: 'Physician matching & reporting' },
    { value: '95%+', label: 'Data Accuracy', description: 'NPI-based provider validation' },
    { value: '70%', label: 'Cost Reduction', description: 'Operational overhead' },
    { value: '80%', label: 'Faster Onboarding', description: 'Per new pharma client' },
  ];

  const services = [
    {
      icon: Database,
      title: 'Multi-Client Data Platform',
      description: 'A metadata-driven Snowflake warehouse architecture that isolates clients, scales with demand, and onboards new pharmaceutical clients in days.',
      outcomes: [
        'Metadata-driven Snowflake data warehouse design',
        'Multi-source integration: PostgreSQL, Salesforce, flat files',
        'Client data isolation with full RBAC and audit logging',
        'Scalable onboarding framework for new pharma clients',
      ],
    },
    {
      icon: Users,
      title: 'Physician Matching & NPI Validation',
      description: 'Automated NPI lookup and provider matching that achieves 95%+ accuracy across client datasets — with full PLD reporting output.',
      outcomes: [
        'Automated NPI Registry lookup and validation',
        'Provider matching engine with 95%+ accuracy',
        'PLD (Physician-Level Data) reporting automation',
        'Cross-client deduplication and identity resolution',
      ],
    },
    {
      icon: GitBranch,
      title: 'Airflow Orchestration & Pipeline Automation',
      description: 'DAG-based scheduling and pipeline automation that ensures data is always fresh, validated, and ready for client-facing analytics.',
      outcomes: [
        'Apache Airflow DAG design for multi-client pipelines',
        'Automated daily/weekly data refresh scheduling',
        'Pipeline monitoring, alerting, and SLA tracking',
        'End-to-end workflow automation from ingestion to delivery',
      ],
    },
    {
      icon: BarChart2,
      title: 'Analytics & Reporting Delivery',
      description: 'Client-facing analytics dashboards and automated PLD compliance reporting that meet pharmaceutical industry audit requirements.',
      outcomes: [
        'Client-facing analytics dashboards per pharmaceutical customer',
        'Campaign performance and HCP engagement reporting',
        'PLD compliance reporting with full audit trail',
        'Trend analysis, benchmarking, and cohort reporting',
      ],
    },
  ];

  const techStack = [
    { category: 'Data Warehouse', items: ['Snowflake', 'PostgreSQL', 'Amazon Redshift'] },
    { category: 'Orchestration', items: ['Apache Airflow', 'dbt', 'AWS Glue'] },
    { category: 'Integrations', items: ['Salesforce', 'REST APIs', 'NPI Lookup API'] },
    { category: 'Analytics', items: ['Python', 'Power BI', 'Tableau'] },
  ];

  const useCases = [
    { name: 'Fragmented Multi-Client Reporting', desc: 'Multiple pharmaceutical clients, each with different data sources and reporting requirements — unified on one platform' },
    { name: 'NPI & PLD Compliance Challenges', desc: 'Manual NPI matching causing errors, delays, and audit failures — automated to 95%+ accuracy with full PLD output' },
    { name: 'Operational Scalability Constraints', desc: 'Manual onboarding taking weeks per new client — reduced to days with configuration-driven architecture' },
    { name: 'Data Quality at Client Scale', desc: 'Inconsistent data from disparate sources causing downstream reporting errors — resolved at ingestion layer' },
  ];

  const whyChoose = [
    {
      icon: Award,
      title: 'NPI & PLD Expertise',
      description: 'We understand physician-level data requirements and compliance obligations — built in from day one.',
    },
    {
      icon: Settings,
      title: 'Configuration-Driven Scale',
      description: 'Add new clients without engineering sprints. Configuration parameters, not code changes.',
    },
    {
      icon: Zap,
      title: 'Multi-Source Integration',
      description: 'PostgreSQL, Salesforce, flat files, REST APIs — we integrate whatever your clients are using.',
    },
    {
      icon: ShieldCheck,
      title: 'Compliance-First Design',
      description: 'RBAC, audit logging, data isolation, and PLD reporting built to withstand pharmaceutical industry audits.',
    },
  ];

  const faqs = [
    {
      q: 'What is PLD reporting?',
      a: 'PLD (Physician-Level Data) reporting tracks marketing activity and spend at the individual NPI level. It is required for pharmaceutical companies to demonstrate that HCP engagement is compliant with industry codes and internal policies. Our automation ensures consistent, auditable PLD output for every client without manual intervention.',
    },
    {
      q: 'Can you integrate with Salesforce CRM?',
      a: 'Yes. Salesforce integration is a standard component of our pharmaceutical analytics platform. We build automated data sync pipelines that pull CRM activity data, match it to NPI records, and incorporate it into PLD reporting and analytics.',
    },
    {
      q: 'How is data isolated between pharmaceutical clients?',
      a: "We use metadata-driven Snowflake architecture with full client segregation enforced at the data layer. Each client's data is partitioned and access-controlled via RBAC and Azure AD groups. Audit logging captures all access events.",
    },
    {
      q: 'How long does it take to onboard a new pharmaceutical client?',
      a: 'Days, not weeks. Our configuration-driven framework means onboarding a new client requires setting configuration parameters and establishing source data connections — not a bespoke engineering engagement for each new account.',
    },
    {
      q: 'Do you support ongoing platform management?',
      a: 'Yes. We offer managed service agreements covering pipeline monitoring, new client onboarding, model updates, and platform expansion as your client base and reporting requirements evolve.',
    },
  ];

  return (
    <>
      <Helmet>
        <title>Analytics & Data Platforms for Pharmaceutical Marketing | Agility</title>
        <meta
          name="description"
          content="Purpose-built data infrastructure for pharmaceutical marketing — NPI validation, PLD compliance reporting, and multi-client analytics at scale. Snowflake + Airflow specialists."
        />
        <link rel="canonical" href="https://www.agilitytech.ai/pharmaceutical-marketing-analytics" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Analytics & Data Platforms for Pharmaceutical Marketing | Agility" />
        <meta property="og:description" content="Purpose-built data infrastructure for pharmaceutical marketing — NPI validation, PLD compliance reporting, and multi-client analytics at scale." />
        <meta property="og:url" content="https://www.agilitytech.ai/pharmaceutical-marketing-analytics" />
        <meta property="og:image" content="https://www.agilitytech.ai/agility hex fonts.png" />
      </Helmet>

      {/* Hero */}
      <section className="relative min-h-[85vh] flex items-center justify-center overflow-hidden bg-gradient-to-br from-indigo-900 via-black to-slate-900">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-indigo-500 via-transparent to-transparent" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-500/10 border border-indigo-500/30 rounded-full mb-8">
            <TrendingUp className="w-4 h-4 text-indigo-400" />
            <span className="text-indigo-300 text-sm font-medium">Pharmaceutical Marketing Analytics</span>
          </div>
          <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 leading-tight">
            Data Platforms for<br />
            <span className="text-teal-400">Pharma Marketing</span>
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-10 leading-relaxed">
            NPI validation, PLD compliance reporting, and multi-client analytics at scale.
            Purpose-built for pharmaceutical marketing — Snowflake and Airflow specialists.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-16">
            <Link
              to="/contact"
              className="group inline-flex items-center justify-center gap-2 bg-teal-500 hover:bg-teal-600 text-white px-8 py-4 rounded-lg font-semibold transition-all"
            >
              Schedule a Discovery Call
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link
              to="/case-studies"
              className="inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 text-white px-8 py-4 rounded-lg font-semibold border border-white/20 transition-all"
            >
              View Case Studies
            </Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-4xl mx-auto">
            {metrics.map((m, i) => (
              <div key={i} className="text-center">
                <div className="text-4xl font-bold text-teal-400 mb-1">{m.value}</div>
                <div className="text-white font-semibold mb-1">{m.label}</div>
                <div className="text-gray-400 text-sm">{m.description}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              What We Build for Pharma Marketing
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Four service areas designed for the unique data complexity of pharmaceutical marketing at client scale.
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            {services.map((s, i) => {
              const Icon = s.icon;
              return (
                <div key={i} className="bg-gray-50 rounded-xl p-8 border border-gray-200 hover:border-teal-400 transition-all">
                  <Icon className="w-10 h-10 text-teal-600 mb-4" />
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{s.title}</h3>
                  <p className="text-gray-600 mb-5 text-sm leading-relaxed">{s.description}</p>
                  <ul className="space-y-2">
                    {s.outcomes.map((o, j) => (
                      <li key={j} className="flex items-start gap-2 text-sm text-gray-700">
                        <CheckCircle2 className="w-4 h-4 text-teal-600 mt-0.5 flex-shrink-0" />
                        {o}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Featured Result */}
      <section className="py-20 bg-gradient-to-br from-indigo-700 to-teal-600 text-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-4">Featured Platform Delivery</h2>
          <p className="text-indigo-100 text-lg mb-10 max-w-2xl mx-auto">
            A multi-client pharmaceutical marketing analytics platform serving 15+ pharma clients with full automation.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {[
              { value: '15+', label: 'Pharma Clients' },
              { value: '100%', label: 'Automation' },
              { value: '95%+', label: 'NPI Accuracy' },
              { value: '70%', label: 'Cost Reduction' },
              { value: '80%', label: 'Faster Onboarding' },
            ].map((stat, i) => (
              <div key={i} className="bg-white/10 rounded-xl p-4 border border-white/20">
                <div className="text-2xl font-bold text-white mb-1">{stat.value}</div>
                <div className="text-indigo-100 text-xs">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Tech Stack */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Technology Stack</h2>
            <p className="text-lg text-gray-600">Snowflake + Airflow at the core, with full flexibility across integrations.</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {techStack.map((t, i) => (
              <div key={i} className="bg-white rounded-xl p-6 border border-gray-200">
                <div className="text-xs font-bold text-gray-500 uppercase tracking-widest mb-4">{t.category}</div>
                <ul className="space-y-2">
                  {t.items.map((item, j) => (
                    <li key={j} className="flex items-center gap-2 text-gray-800 text-sm font-medium">
                      <div className="w-1.5 h-1.5 bg-teal-500 rounded-full" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Common Use Cases</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {useCases.map((uc, i) => (
              <div key={i} className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                <div className="text-teal-700 font-bold mb-2">{uc.name}</div>
                <p className="text-gray-600 text-sm leading-relaxed">{uc.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose */}
      <section className="py-24 bg-gradient-to-br from-gray-900 to-black text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              Why Pharma Marketing Teams Choose <span className="text-teal-400">Agility</span>
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {whyChoose.map((item, i) => {
              const Icon = item.icon;
              return (
                <div key={i} className="bg-white/5 rounded-xl p-8 border border-white/10 hover:border-teal-500/50 transition-all">
                  <Icon className="w-10 h-10 text-teal-400 mb-4" />
                  <h3 className="text-lg font-bold mb-3">{item.title}</h3>
                  <p className="text-gray-300 text-sm leading-relaxed">{item.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">
            Frequently Asked Questions
          </h2>
          <div className="space-y-6">
            {faqs.map((f, i) => (
              <div key={i} className="bg-white rounded-xl p-8 border border-gray-200">
                <h3 className="text-lg font-bold text-gray-900 mb-3">{f.q}</h3>
                <p className="text-gray-600 leading-relaxed">{f.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-gradient-to-br from-indigo-700 to-teal-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Ready to Automate Your Pharma Analytics?
          </h2>
          <p className="text-xl text-white/80 mb-8 max-w-2xl mx-auto">
            Tell us about your client reporting and NPI challenges. We'll scope a plan in 48 hours.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 bg-white text-indigo-700 hover:bg-gray-100 px-8 py-4 rounded-lg font-semibold transition-all text-lg"
          >
            Schedule a Discovery Call
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>
    </>
  );
};

export default PharmaceuticalMarketingAnalytics;
